import React, { useState } from 'react';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaUser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const SignupPage = () => {
  const [fullName, setFullName] = useState('');
  const [gender, setGender] = useState('');
  const [ageCategory, setAgeCategory] = useState('');
  const [position, setPosition] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [hasDisability, setHasDisability] = useState('');
  const [disabilityTypes, setDisabilityTypes] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [cacheKey, setCacheKey] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const navigate = useNavigate();

  const handleDisabilityChange = (e) => {
    const value = e.target.value;
    setDisabilityTypes((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const handleSendOtp = async () => {
    if (!email) {
      setError('Please enter an email address.');
      return;
    }
    setIsSendingOtp(true);
    setError('');
    setOtpError('');
    try {
      const response = await fetch(`${backendUrl}login/send-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        setCacheKey(data.cache_key);
        setShowOtpInput(true);
        localStorage.setItem('pending_cache_key', data.cache_key);
        localStorage.setItem('pending_cache_email', email);
      } else {
        const errorMsg = data.email || data.detail || 'Failed to send OTP. Please check the email address or try again.';
        setError(errorMsg);
        console.error('Send OTP failed:', { status: response.status, data });
      }
    } catch (err) {
      const errorMsg = `Failed to connect to the server: ${err.message}. Please ensure the backend server is running at ${backendUrl}.`;
      setError(errorMsg);
      console.error('Send OTP error:', err, { backendUrl });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpError('');
    const finalCacheKey = cacheKey || localStorage.getItem('pending_cache_key');
    const finalEmail = email || localStorage.getItem('pending_cache_email');
    if (!finalCacheKey || !finalEmail) {
      setOtpError('OTP session expired. Please request a new OTP.');
      setShowOtpInput(false);
      localStorage.removeItem('pending_cache_key');
      localStorage.removeItem('pending_cache_email');
      return;
    }
    try {
      const response = await fetch(`${backendUrl}login/verify-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: finalEmail, otp, cache_key: finalCacheKey }),
      });
      const data = await response.json();
      if (response.ok) {
        setIsEmailVerified(true);
        setShowOtpInput(false);
        setOtp('');
        localStorage.removeItem('pending_cache_key');
        localStorage.removeItem('pending_cache_email');
      } else {
        const errorMsg = data.detail || 'Invalid or expired OTP.';
        setOtpError(errorMsg);
        console.error('Verify OTP failed:', { status: response.status, data });
      }
    } catch (err) {
      const errorMsg = `Failed to verify OTP: ${err.message}. Please check your connection to ${backendUrl}.`;
      setOtpError(errorMsg);
      console.error('Verify OTP error:', err, { backendUrl });
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!isEmailVerified) {
      setError('Please verify your email before registering.');
      return;
    }
    if (!fullName || fullName.length < 3) {
      setError('Full name is required (min 3 chars).');
      return;
    }
    if (!gender) {
      setError('Gender is required.');
      return;
    }
    if (!ageCategory) {
      setError('Age category is required.');
      return;
    }
    if (!position) {
      setError('Position is required.');
      return;
    }
    if (!contactNumber) {
      setError('Contact number is required.');
      return;
    }
    if (!hasDisability) {
      setError('Please specify if you have disabilities.');
      return;
    }
    if (hasDisability === 'yes' && disabilityTypes.length === 0) {
      setError('Please select at least one disability type.');
      return;
    }
    if (!email) {
      setError('Email is required.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }
    try {
      const response = await fetch(`${backendUrl}login/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: fullName,
          gender,
          age_category: ageCategory,
          position,
          contact_number: contactNumber,
          has_disability: hasDisability,
          disability_types: hasDisability === 'yes' ? disabilityTypes : [],
          email,
          password,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        alert('Registration successful. Please login!');
        navigate('/LoginPage', { replace: true });
      } else {
        const errorMsg = data.email || data.password || data.detail || 'Signup failed. Please try again.';
        setError(errorMsg);
        console.error('Signup failed:', { status: response.status, data });
        if (errorMsg.includes('Email must be verified')) {
          setIsEmailVerified(false);
          setError(errorMsg + ' Please verify again.');
        }
      }
    } catch (err) {
      const errorMsg = `Failed to connect to the server: ${err.message}. Please check your connection to ${backendUrl}.`;
      setError(errorMsg);
      console.error('Signup error:', err, { backendUrl });
    }
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem',
    }}>
      <img
        src="/view.jpg"
        alt="Background"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: 1,
          zIndex: -1,
        }}
      />
      <div style={{
        width: '100%',
        maxWidth: '600px',
        backgroundColor: '#ffffffe6',
        opacity: 0.9,
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 0 15px 3px rgba(0, 0, 0, 0.1)',
        padding: '1.5rem',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap',
          }}>
            <img src="/dnmg.png" alt="DRRDSS Logo" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            <img src="/timor.png" alt="Timor Logo" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            <h1 style={{
              fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', margin: 0
            }}>
              Disaster Risk Reduction Decision Support System
            </h1>
          </div>
        </div>
        <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
            }}>
              <FaUser size={14} style={{ color: '#9ca3af' }} />
              Full Name
            </label>
            <input
              type="text"
              placeholder="Enter full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={{
                height: '2.5rem',
                padding: '0.5rem',
                borderRadius: '0.5rem',
                border: '1px solid #d1d5db',
                outline: 'none',
                fontSize: '0.875rem',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(4px)',
              }}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
              }}>
                Gender
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                style={{
                  height: '2.5rem',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  outline: 'none',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(4px)',
                }}
                required
              >
                <option value="" disabled>Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
              }}>
                Age Category
              </label>
              <select
                value={ageCategory}
                onChange={(e) => setAgeCategory(e.target.value)}
                style={{
                  height: '2.5rem',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  outline: 'none',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(4px)',
                }}
                required
              >
                <option value="" disabled>Select Age Category</option>
                <option value="below_18">Below 18</option>
                <option value="19-50">19-50</option>
                <option value="above_50">More than 50</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
              }}>
                Position
              </label>
              <input
                type="text"
                placeholder="Enter position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                style={{
                  height: '2.5rem',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  outline: 'none',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(4px)',
                }}
                required
              />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
              }}>
                Contact Number
              </label>
              <input
                type="tel"
                placeholder="Enter contact number"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                style={{
                  height: '2.5rem',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  outline: 'none',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(4px)',
                }}
                required
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
            }}>
              Do you consider yourself as a person with disabilities?
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                <input
                  type="radio"
                  name="hasDisability"
                  value="yes"
                  checked={hasDisability === 'yes'}
                  onChange={(e) => setHasDisability(e.target.value)}
                  required
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                <input
                  type="radio"
                  name="hasDisability"
                  value="no"
                  checked={hasDisability === 'no'}
                  onChange={(e) => setHasDisability(e.target.value)}
                  required
                />
                No
              </label>
            </div>
          </div>
          {hasDisability === 'yes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
              }}>
                Select the types of disabilities:
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                {['Seeing', 'Listening', 'Walking', 'Speaking', 'Others'].map((type) => (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                    <input
                      type="checkbox"
                      value={type}
                      checked={disabilityTypes.includes(type)}
                      onChange={handleDisabilityChange}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
            }}>
              <FaEnvelope size={14} style={{ color: '#9ca3af' }} />
              Email Address
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isEmailVerified}
                style={{
                  flex: 1,
                  height: '2.5rem',
                  padding: '0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  outline: 'none',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(4px)',
                }}
                required
              />
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={isSendingOtp || !email || isEmailVerified}
                style={{
                  height: '2.5rem',
                  padding: '0 1rem',
                  background: isEmailVerified ? 'linear-gradient(to right, #10b981, #059669)' : (isSendingOtp || !email ? '#d1d5db' : 'linear-gradient(to right, #4f46e5, #a855f7)'),
                  color: 'white',
                  fontWeight: '600',
                  borderRadius: '0.5rem',
                  border: 'none',
                  cursor: isSendingOtp || !email || isEmailVerified ? 'not-allowed' : 'pointer',
                  fontSize: '0.75rem',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                }}
              >
                {isEmailVerified ? 'Verified' : (isSendingOtp ? 'Sending...' : 'Verify Email')}
              </button>
            </div>
            {showOtpInput && (
              <div style={{
                marginTop: '0.5rem',
                padding: '1rem',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '0.5rem',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{
                    color: '#374151',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    textAlign: 'center',
                  }}>
                    Enter the 6-digit OTP sent to <b>{email}</b>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength="6"
                    autoFocus
                    autoComplete="one-time-code"
                    name="otp-code"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    style={{
                      width: '100%',
                      height: '2.5rem',
                      fontSize: '1rem',
                      textAlign: 'center',
                      borderRadius: '0.5rem',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      backdropFilter: 'blur(4px)',
                    }}
                    required
                  />
                  {otpError && <div style={{ color: '#dc3545', fontSize: '0.75rem', textAlign: 'center' }}>{otpError}</div>}
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    style={{
                      width: '100%',
                      height: '2.5rem',
                      background: 'linear-gradient(to right, #4f46e5, #a855f7)',
                      color: 'white',
                      fontWeight: '600',
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    Verify OTP
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOtp('');
                      handleSendOtp();
                    }}
                    style={{
                      width: '100%',
                      height: '2rem',
                      marginTop: '0.5rem',
                      background: '#e5e7eb',
                      color: '#333',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                    }}
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', position: 'relative' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.75rem', fontWeight: '500'
            }}>
              <FaLock size={14} style={{ color: '#9ca3af' }} />
              Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              name="new-password"
              style={{
                height: '2.5rem',
                padding: '0.5rem 2rem 0.5rem 0.5rem',
                borderRadius: '0.5rem',
                border: '1px solid #d1d5db',
                outline: 'none',
                fontSize: '0.875rem',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(4px)',
              }}
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              style={{
                position: 'absolute',
                right: '0.5rem',
                top: '60%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
              }}
            >
              {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
          {error && <div style={{ color: '#dc3545', fontSize: '0.75rem', marginTop: '0.25rem' }}>{error}</div>}
          <button
            type="submit"
            style={{
              width: '100%',
              height: '2.5rem',
              background: 'linear-gradient(to right, #4f46e5, #a855f7)',
              color: 'white',
              fontWeight: '600',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s',
            }}
          >
            Sign Up
          </button>
          <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
            <p style={{ color: '#374151', fontSize: '0.75rem' }}>
              Already have an account?{' '}
              <a
                href="/LoginPage"
                style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: '500' }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/LoginPage');
                }}
              >
                Login here
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupPage;